// src/components/AppointmentsPreview.jsx
import { useMemo, useState } from 'react'; // Removed useState, useEffect, useRef, useCallback
import { Link, useNavigate } from 'react-router-dom';
import { isValid } from 'date-fns'; // Import isValid
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { useAppointment } from '../contexts/AppointmentContext';
import { useNavigateWithContext } from '../hooks/useNavigateWithContext';
import GenericConfirmationModal  from '../components/GenericConfirmationModal';
import Spinner from '../components/Spinner';
import { getAppointmentActionState, isUpcomingAppointment, refundFailureMessage } from '../utilities/appointmentRules';
import { useMinuteTicker } from '../hooks/useMinuteTicker';


// Helper function to determine color based on status
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'scheduled':
    case 'created':
    case 'waiting': // Added Waiting status
      return 'border-blue-500'; // Upcoming/Active
    case 'completed':
      return 'border-green-500'; // Completed
    case 'cancelled': // Corrected spelling
    case 'missed':
      return 'border-red-500'; // Cancelled/Missed
    default:
      return 'border-gray-300'; // Default
  }
};

// Accept props from Dashboard
function AppointmentsPreview() {
  const { appointments, isLoading, error, refreshAppointments, cancelAppointment } = useAppointment();
  const navigateWithContext  = useNavigateWithContext();
  const [ showCancelConfirm, setShowCancelConfirm ] = useState({show: false, apptId: null});
  const [isCanceling, setIsCanceling] = useState(false);
  const navigate = useNavigate();
  const PREVIEW_LIMIT = 3; // Show the next 3 upcoming appointments
  const appointmentClock = useMinuteTicker();

  // Filter the appointments prop to get the preview list using useMemo
  const previews = useMemo(() => {
    if (!Array.isArray(appointments)) return [];

    return appointments
      .filter(appt => appt.date_appt && isValid(appt.date_appt) && isUpcomingAppointment(appt))
      .sort((a, b) => new Date(a.date_appt) - new Date(b.date_appt))
      .slice(0, PREVIEW_LIMIT);
  }, [appointments, appointmentClock]); // Recalculate as join/cancel windows change

  const handleCancelAppt = (apptId) => {
    setShowCancelConfirm({
      show: true,
      apptId: apptId,
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-primary font-semibold">Upcoming Appointments</h4>
        {/* Refresh button uses the passed handler */}
        <button
          className={`text-xl text-gray-400 hover:text-primary-light ${isLoading ? 'animate-spin' : ''}`} // Add spin animation
          onClick={refreshAppointments} // Use the passed handler
          title="Refresh Appointments"
          disabled={isLoading} // Disable while loading
        >
          <FontAwesomeIcon icon={faArrowsRotate} />
        </button>
      </div>

      {/* Loading State - Use prop */}
      {isLoading && <div className="flex justify-center items-center h-32"><Spinner /></div>}

      {/* Error State - Use prop */}
      {!isLoading && error && <p className="text-red-500 text-sm">{error}</p>}

      {/* Appointments List - Use filtered previews */}
      {!isLoading && !error && (
        <div className="space-y-3 flex-grow">
          {previews.length > 0 ? (
	            previews.map((appt) => {
	              // Ensure date_appt is valid before proceeding
	              if (!appt.date_appt || !isValid(appt.date_appt)) {
	                return null;
	              }

	              const statusColor = getStatusColor(appt.status);
	              const { isJoinable, isCancellable } = getAppointmentActionState(appt);
              
              return (
                <div
                  key={appt.appointment_id} // Use appointment ID as key
                  className={`bg-white p-3 rounded-lg shadow-sm border-l-4 ${statusColor} flex flex-col sm:flex-row sm:items-center sm:justify-between`}
                >
                  {/* Appointment Info */}
                  <div className="flex-grow mb-2 sm:mb-0">
                     <p className="font-semibold text-sm text-gray-800 truncate" title={appt.complaint}>
                       {/* Display complaint or default text */}
                       Appointment
                     </p>
                     <p className="text-xs text-gray-600">
                      {/* Format the Date object */}
                       {appt.date_appt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} {/* Removed year for brevity */}
                       {' at '}
                       {appt.date_appt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                       {' · '} Status: {appt.status}
                     </p>
                  </div>
                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2 sm:mt-0">
                    {isJoinable && (
                      <button
                        onClick={() => navigateWithContext(appt.appointment_id, "/encounter", "appointment")}
                        className="bg-green-600 text-white px-3 py-1 text-sm rounded hover:bg-green-700 whitespace-nowrap w-full sm:w-auto"
                      >
                        Join Now
                      </button>
                    )}
                    {isCancellable && handleCancelAppt && ( 
                      <button
                        onClick={() => handleCancelAppt(appt.appointment_id)}
                        className="bg-red-500 text-white px-3 py-1 text-sm rounded hover:bg-red-600 whitespace-nowrap w-full sm:w-auto"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              );
            })
	          ) : (
	            <div className="text-center py-4 border border-gray-200 bg-white rounded">
	              <p className="text-sm text-gray-600 mb-3">No appointment is booked yet.</p>
	              <button
	                type="button"
	                onClick={() => navigate('/schedule')}
	                className="px-3 py-2 bg-button text-white rounded hover:bg-button-light text-sm"
	              >
	                Schedule appointment
	              </button>
	            </div>
	          )}
        </div>
      )}

      {/* View All Link */}
       {!isLoading && (
         <div className="mt-auto pt-2 text-right">
           <button onClick={() => navigate("/appointments")} className="text-sm text-primary hover:underline font-medium">
             View All Appointments <FontAwesomeIcon icon={faChevronRight} size="xs" />
           </button>
         </div>
       )}

       {showCancelConfirm?.show && (
        <GenericConfirmationModal
          isOpen={showCancelConfirm.show}
          onCancel={() => setShowCancelConfirm({})}
          onConfirm={async () => {
            setIsCanceling(true);
            const result = await cancelAppointment(showCancelConfirm.apptId);
            setIsCanceling(false);
            if (result?.error) {
              toast.error(result.error);
              return;
            }
            setShowCancelConfirm({});
            toast.success('Appointment canceled.');
            const refundMsg = refundFailureMessage(result);
            if (refundMsg) toast.error(refundMsg, { duration: 10000 });
          }}
          title="Cancel Appointment"
          message="Are you sure you want to cancel this appointment?"
          textCancel="No"
          textConfirm={isCanceling ? 'Canceling...' : 'Yes'}
        />
      )}
    </div>
  );
}

export default AppointmentsPreview;
